/**
 * Who a client is acting as, and how one signs in.
 *
 * ── This file used to promise there were no secrets. That is no longer true ────
 *
 * An earlier design verified Google ID tokens server-side, and the only sign-in
 * value it carried — an OAuth client id — was public by construction. These docs
 * said so at length. LunaPin now serves **one account whose password comes from the
 * operator's environment**, so:
 *
 *  - [SignInRequest.password] is a secret in flight. Nothing on either side may log
 *    it, put it in a URL, or echo it back in a response.
 *  - [SignInResponse.sessionId] is a secret at rest as well as in flight — one string
 *    is enough to *be* the account — and it is why that response is a type of its own
 *    rather than a field on [SessionState].
 *  - Nothing in [SessionState] is a secret, and that is now a property to *maintain*
 *    rather than one that falls out of the design. Every field here travels to an
 *    unauthenticated browser, so no credential may ever be added to it — not the
 *    username, not a hint, not a length.
 *
 * @see ApiRoutes.SESSION
 */
package se.soderbjorn.lunapin.clientserver

import kotlinx.serialization.Serializable

/**
 * One account.
 *
 * @property id the account's own identifier, and the key everything per-user is
 *   stored against (see [UiSettingsState]). A `Long` rather than the username, so
 *   that changing the username is not a data migration.
 * @property displayName what a person is called on screen.
 * @property username the name this account signs in with — the server's
 *   `LUNAPIN_USERNAME`, echoed back to the client that has *already* proved it knows
 *   the password.
 *
 *   Not carried in the signed-out [SessionState], and that is the rule to keep: a
 *   client learning the configured username without signing in would be handed half
 *   the credential pair for free.
 */
@Serializable
data class User(
    val id: Long,
    val displayName: String,
    val username: String,
)

/**
 * The answer to "who am I", plus whether this deployment has a way in at all.
 *
 * Both halves travel together because the sign-in surface needs both to decide what
 * to render, and asking twice would mean a frame in which it knows it is signed out
 * but not yet whether it may offer a way in — which is exactly the frame that
 * flickers.
 *
 * @property user the account this client is acting as, or `null` for nobody. Null is
 *   the state every launch starts in, and an ordinary answer from
 *   [ApiRoutes.SESSION] rather than a failure.
 * @property isSignInConfigured whether this server was given both `LUNAPIN_USERNAME`
 *   and `LUNAPIN_PASSWORD`, and can therefore let anybody in.
 *
 *   **Sent rather than computed, and that is a change worth flagging.** While there
 *   were two sign-in methods this was derived from the two fields describing them,
 *   on the grounds that a third field which must agree with two others is a third
 *   field that can disagree with them. With one method there is nothing left to
 *   derive it from, so the server states it — and the client still must not decide it
 *   for itself, because only the server knows which variables it was handed. The same
 *   bundle is served by a developer's laptop and by the deployment.
 *
 *   False is a real, reachable state — a server started with neither variable, which
 *   is what a fresh clone has — and it is the one the signed-out surface has to
 *   *explain* rather than merely render, since there is no button that could help.
 *   Deliberately says nothing about *which* of the two variables is missing: that
 *   distinction is only useful to the operator, who has the startup log, and a
 *   browser is the wrong place to publish a partial configuration.
 */
@Serializable
data class SessionState(
    val user: User? = null,
    val isSignInConfigured: Boolean = false,
)

/**
 * Sign in as this deployment's single account.
 *
 * The whole credential, in one POST body over TLS. Not query parameters, which end
 * up in access logs, referrer headers and shell history; not a `Authorization: Basic`
 * header, which would have to be re-sent on every request and so would live in the
 * client for as long as the tab did. The session cookie is what carries the caller
 * afterwards — see the server's `Sessions.kt` — and this body is read exactly once.
 *
 * @property username the configured `LUNAPIN_USERNAME`.
 * @property password the configured `LUNAPIN_PASSWORD`.
 *
 *   **Never log either of these, at any level, on either side.** They do not expire
 *   the way a session id or an ID token does: this pair is the account until an
 *   operator changes an environment variable and redeploys. A password in a log is
 *   therefore permanent, and logs are the one place secrets get copied to by
 *   accident — a crash report, a support paste, a `curl -v` in a ticket.
 * @property wantsSessionId whether the caller needs [SignInResponse.sessionId] in the
 *   body, because it has no cookie jar. **Defaults to false, and the default is the
 *   security property** rather than a convenience.
 *
 *   A browser must never be handed the session id: it is already holding it as an
 *   `HttpOnly` cookie, and a copy in a response body is a copy page script can read —
 *   which is the one attribute standing between an XSS on our own origin and a stolen
 *   session. So the decision is made by *the caller saying what it is*, and the
 *   dangerous direction is the one that requires an explicit request. A client that
 *   forgets to ask gets nothing; a browser that never asks can never be handed one.
 *
 *   **Sniffing the caller was considered and rejected.** `Origin`, `Sec-Fetch-Mode`,
 *   `User-Agent` and "did a cookie arrive?" are all guesses about who is calling, and a
 *   guess that is wrong once hands a session id to page script permanently. The client
 *   knows what it is; there is no reason for the server to estimate it.
 */
@Serializable
data class SignInRequest(
    val username: String,
    val password: String,
    val wantsSessionId: Boolean = false,
)

/**
 * What a successful sign-in answers: the new session, and — only if asked — the id.
 *
 * ── Why the id is not simply a field on [SessionState] ────────────────────────
 *
 * [SessionState] is also the body of `ApiRoutes.SESSION`, which answers *every* caller
 * including an unauthenticated one, and its own docs commit to nothing in it being a
 * secret. Adding a nullable session id there would put a credential-shaped field on the
 * one response the server hands out freely, one careless `sessionStateFor` away from
 * populating it. A separate type for the one response that mints a session keeps that
 * impossible rather than merely unlikely, and it makes the grep for "who can produce a
 * session id" answer with a single class.
 *
 * @property session who the caller is now acting as. The same value the pre-bearer
 *   design returned on its own, so the browser path is unchanged apart from one level of
 *   nesting.
 * @property sessionId the opaque id of the freshly minted session, for a caller that
 *   asked via [SignInRequest.wantsSessionId], and `null` for one that did not.
 *
 *   **A bearer credential for the whole account.** Never logged, on either side; never
 *   put in a query string, a URL, an analytics event or a crash report. A client that
 *   receives one stores it where its platform keeps credentials (the iOS Keychain,
 *   Android app-private storage) and sends it as `Authorization: Bearer` — see the
 *   server's `Sessions.kt`.
 *
 *   Null rather than empty for "not asked for", so that "the server did not give me one" and
 *   "the server gave me a blank one" cannot be confused by a client deciding whether it can
 *   sync.
 *
 *   On the wire it arrives as an explicit `"sessionId": null` rather than an absent key,
 *   because Ktor's server `Json` encodes defaults. That is fine — a null is not a credential
 *   — and it is worth knowing rather than assuming, since the *request* side behaves the
 *   opposite way: the client's `Json` does not encode defaults, so a browser's sign-in body
 *   omits [SignInRequest.wantsSessionId] entirely.
 */
@Serializable
data class SignInResponse(
    val session: SessionState,
    val sessionId: String? = null,
)
